import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, addDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { HiChevronLeft, HiChevronRight, HiPlus, HiOutlineTrash } from 'react-icons/hi2';
import { HiOutlineCog6Tooth } from 'react-icons/hi2';
import { IoCloseCircle } from 'react-icons/io5';
import useAdminAuth from '../../../hooks/useAdminAuth';
import { friendlyDate } from '../../../utils/dateFormat';

const DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 8시~21시

const EVENT_COLORS = ['#6366F1', '#EC4899', '#14B8A6', '#F97316', '#8B5CF6'];

const AdminSchedulePage = () => {
  const navigate = useNavigate();
  const { filterByOwner, isReadOnly } = useAdminAuth();
  const [classes, setClasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [viewMode, setViewMode] = useState('monthly'); // 'weekly' | 'monthly'
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState({ title: '', date: '', time: '', endTime: '', color: '#6366F1', description: '' });
  const [selectedDate, setSelectedDate] = useState(null); // 월간 선택 날짜

  // 주간 기준 날짜 (해당 주의 월요일)
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
  });

  // 월간 기준
  const [calMonth, setCalMonth] = useState(new Date());

  useEffect(() => {
    const load = async () => {
      const [classSnap, eventSnap] = await Promise.all([
        getDocs(collection(db, 'classes')),
        getDocs(collection(db, 'academyEvents')),
      ]);
      const all = classSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setClasses(filterByOwner(all));
      setEvents(eventSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoaded(true);
    };
    load();
  }, []);

  // ─── 학원 일정 CRUD ───
  const addEvent = async () => {
    if (!eventForm.title || !eventForm.date) return;
    const data = { ...eventForm, createdAt: Timestamp.now() };
    const docRef = await addDoc(collection(db, 'academyEvents'), data);
    setEvents([...events, { id: docRef.id, ...data }]);
    setShowEventModal(false);
    setEventForm({ title: '', date: '', time: '', endTime: '', color: '#6366F1', description: '' });
  };
  const removeEvent = async (ev) => {
    if (!window.confirm(`'${ev.title}' 일정을 삭제하시겠습니까?`)) return;
    await deleteDoc(doc(db, 'academyEvents', ev.id));
    setEvents(events.filter((e) => e.id !== ev.id));
  };

  const getEventsForDate = (dateStr) => events.filter((e) => e.date === dateStr);

  // ─── 주간 헬퍼 ───
  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const formatDateStr = (d) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const getWeeklySchedules = (dayName, dateStr) => {
    const items = [];
    classes.forEach((cls) => {
      // 정규 수업
      (cls.schedule || []).forEach((s) => {
        if (s.day === dayName) {
          items.push({
            type: 'regular',
            className: cls.name,
            color: cls.color || '#4A7CFE',
            time: s.time,
            endTime: s.endTime || '',
            room: cls.room || '',
            teacherName: cls.teacherName || '',
          });
        }
      });
      // 보강 수업
      (cls.supplements || []).forEach((s) => {
        if (s.date === dateStr) {
          items.push({
            type: 'supplement',
            className: cls.name,
            color: cls.color || '#4A7CFE',
            time: s.time,
            endTime: s.endTime || '',
            room: cls.room || '',
            teacherName: cls.teacherName || '',
          });
        }
      });
    });
    // 학원 일정
    getEventsForDate(dateStr).forEach((ev) => {
      items.push({
        type: 'event',
        className: ev.title,
        color: ev.color || '#6366F1',
        time: ev.time || '',
        room: ev.description || '',
        eventId: ev.id,
        eventData: ev,
      });
    });
    return items;
  };

  const getItemsForHour = (dayName, dateStr, hour) => {
    return getWeeklySchedules(dayName, dateStr).filter((item) => {
      const h = parseInt(item.time?.split(':')[0]);
      return h === hour;
    });
  };

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };
  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };
  const goThisWeek = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    setWeekStart(new Date(d.getFullYear(), d.getMonth(), diff));
  };

  // ─── 월간 헬퍼 ───
  const getCalDays = () => {
    const y = calMonth.getFullYear();
    const m = calMonth.getMonth();
    const firstDay = new Date(y, m, 1).getDay();
    const lastDate = new Date(y, m + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= lastDate; d++) days.push(d);
    return days;
  };

  const getMonthlySchedules = (dayNum) => {
    if (!dayNum) return [];
    const y = calMonth.getFullYear();
    const m = calMonth.getMonth();
    const dateObj = new Date(y, m, dayNum);
    const dayName = DAYS[dateObj.getDay()];
    const dateStr = formatDateStr(dateObj);
    const items = [];
    classes.forEach((cls) => {
      (cls.schedule || []).forEach((s) => {
        if (s.day === dayName) {
          items.push({
            type: 'regular',
            classId: cls.id,
            className: cls.name,
            color: cls.color || '#4A7CFE',
            time: s.time,
            endTime: s.endTime || '',
            room: cls.room || '',
            teacherName: cls.teacherName || '',
            studentCount: cls.students || cls.studentIds?.length || 0,
          });
        }
      });
      (cls.supplements || []).forEach((s) => {
        if (s.date === dateStr) {
          items.push({
            type: 'supplement',
            classId: cls.id,
            className: cls.name,
            color: cls.color || '#4A7CFE',
            time: s.time,
            endTime: s.endTime || '',
            room: cls.room || '',
            teacherName: cls.teacherName || '',
            studentCount: cls.students || cls.studentIds?.length || 0,
          });
        }
      });
    });
    // 학원 일정
    getEventsForDate(dateStr).forEach((ev) => {
      items.push({
        type: 'event',
        className: ev.title,
        color: ev.color || '#6366F1',
        time: ev.time || '',
        endTime: ev.endTime || '',
        room: ev.description || '',
        eventId: ev.id,
        eventData: ev,
      });
    });
    return items;
  };

  const todayStr = formatDateStr(new Date());
  const weekDates = getWeekDates();

  // 주간 범위 텍스트
  const weekEndDate = new Date(weekStart);
  weekEndDate.setDate(weekEndDate.getDate() + 6);
  const weekLabel = `${weekStart.getMonth() + 1}/${weekStart.getDate()} ~ ${weekEndDate.getMonth() + 1}/${weekEndDate.getDate()}`;

  return (
    <Container>
      <TopRow>
        <PageTitle>일정 관리</PageTitle>
        <TopRowCenter>
          {viewMode === 'weekly' ? (
            <>
              <NavBtn onClick={prevWeek}><HiChevronLeft size={18} /></NavBtn>
              <NavTitle>{weekStart.getFullYear()}년 {weekLabel}</NavTitle>
              <NavBtn onClick={nextWeek}><HiChevronRight size={18} /></NavBtn>
              <TodayBtn onClick={goThisWeek}>오늘</TodayBtn>
            </>
          ) : (
            <>
              <NavBtn onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))}><HiChevronLeft size={18} /></NavBtn>
              <NavTitle>{calMonth.getFullYear()}년 {calMonth.getMonth() + 1}월</NavTitle>
              <NavBtn onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))}><HiChevronRight size={18} /></NavBtn>
              <TodayBtn onClick={() => setCalMonth(new Date())}>오늘</TodayBtn>
            </>
          )}
        </TopRowCenter>
        <TopRowRight>
          {!isReadOnly && (
            <AddBtn onClick={() => setShowEventModal(true)}>
              <HiPlus size={16} /> 학원 일정 추가
            </AddBtn>
          )}
          <ViewToggle>
            <ViewBtn $active={viewMode === 'weekly'} onClick={() => setViewMode('weekly')}>주간</ViewBtn>
            <ViewBtn $active={viewMode === 'monthly'} onClick={() => setViewMode('monthly')}>월간</ViewBtn>
          </ViewToggle>
        </TopRowRight>
      </TopRow>

      {/* ═══ 주간 보기 ═══ */}
      {viewMode === 'weekly' && (
        <>

          <WeekCard>
            <WeekTable>
              <thead>
                <tr>
                  <WTimeTh>시간</WTimeTh>
                  {weekDates.map((d, i) => {
                    const ds = formatDateStr(d);
                    const isToday = ds === todayStr;
                    return (
                      <WDayTh key={i} $today={isToday}>
                        <WDayName $today={isToday} $sun={i === 6} $sat={i === 5}>
                          {DAYS[(i + 1) % 7 === 0 ? 0 : (i + 1)]}
                        </WDayName>
                        <WDayDate $today={isToday}>{d.getMonth() + 1}/{d.getDate()}</WDayDate>
                      </WDayTh>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {HOURS.map((hour) => (
                  <tr key={hour}>
                    <WTimeCell>{hour}:00</WTimeCell>
                    {weekDates.map((d, i) => {
                      const dayIdx = d.getDay();
                      const dayName = DAYS[dayIdx];
                      const dateStr = formatDateStr(d);
                      const items = getItemsForHour(dayName, dateStr, hour);
                      const isToday = dateStr === todayStr;
                      return (
                        <WDayCell key={i} $today={isToday}>
                          {items.map((item, idx) => (
                            <WBlock key={idx} $color={item.color} $supplement={item.type === 'supplement'} $event={item.type === 'event'}>
                              <WBlockTop>
                                <WBlockName>{item.className}</WBlockName>
                                {item.type === 'event' && !isReadOnly && (
                                  <WDeleteBtn onClick={() => removeEvent(item.eventData)}><HiOutlineTrash size={11} /></WDeleteBtn>
                                )}
                              </WBlockTop>
                              <WBlockTime>
                                {item.time}{item.endTime ? ` - ${item.endTime}` : ''}{item.type === 'supplement' ? ' (보강)' : item.type === 'event' ? '' : ''}
                              </WBlockTime>
                              {item.room && <WBlockRoom>{item.room}</WBlockRoom>}
                              {item.type === 'event' && <WEventBadge>학원</WEventBadge>}
                            </WBlock>
                          ))}
                        </WDayCell>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </WeekTable>
          </WeekCard>

        </>
      )}

      {/* ═══ 월간 보기 ═══ */}
      {viewMode === 'monthly' && (
        <>
          <MonthCard>
            <MWeekRow>
              {DAYS.map((d, di) => (
                <MWeekCell key={d} $sun={di === 0} $sat={di === 6}>{d}</MWeekCell>
              ))}
            </MWeekRow>
            <MGrid>
              {getCalDays().map((dayNum, i) => {
                const colIdx = i % 7;
                if (!dayNum) return <MDayCell key={i} />;
                const dateObj = new Date(calMonth.getFullYear(), calMonth.getMonth(), dayNum);
                const dateStr = formatDateStr(dateObj);
                const isToday = dateStr === todayStr;
                const items = getMonthlySchedules(dayNum);
                return (
                  <MDayCell key={i} $hasItems={items.length > 0} $selected={selectedDate === dateStr} onClick={() => setSelectedDate(selectedDate === dateStr ? null : dateStr)} style={{ cursor: 'pointer' }}>
                    <MDayNum $today={isToday} $sun={colIdx === 0} $sat={colIdx === 6}>{dayNum}</MDayNum>
                    <MEventList>
                      {items.map((item, idx) => (
                        <MEventTag key={idx} $color={item.color} $supplement={item.type === 'supplement'} $event={item.type === 'event'}>
                          {item.type === 'event' ? <MEventEmoji>📌</MEventEmoji> : item.type === 'supplement' ? <MSupDot /> : <MEventDot />}
                          <MEventText $event={item.type === 'event'}>{item.className}{item.time ? ` ${item.time}${item.endTime ? ` - ${item.endTime}` : ''}` : ''}</MEventText>
                          {item.type === 'supplement' && <MSupBadge>보강</MSupBadge>}
                        </MEventTag>
                      ))}
                    </MEventList>
                  </MDayCell>
                );
              })}
            </MGrid>
          </MonthCard>

        </>
      )}

      {/* 선택된 날짜 상세 모달 */}
      {selectedDate && (() => {
        const selDayNum = parseInt(selectedDate.split('-')[2]);
        const selItems = getMonthlySchedules(selDayNum);
        return (
          <Overlay onClick={() => setSelectedDate(null)}>
            <DetailModal onClick={(e) => e.stopPropagation()}>
              <DetailHeader>
                <DetailTitle>{friendlyDate(selectedDate)}</DetailTitle>
                <DetailCloseBtn onClick={() => setSelectedDate(null)}><IoCloseCircle size={22} /></DetailCloseBtn>
              </DetailHeader>
              {selItems.length === 0 ? (
                <DetailEmpty>이 날짜에 등록된 일정이 없습니다</DetailEmpty>
              ) : (
                <DetailList>
                  {selItems.map((item, idx) => (
                    <DetailItem key={idx}>
                      <DetailColorBar $color={item.color} />
                      <DetailItemInfo>
                        <DetailItemName>
                          {item.className}
                          {item.type === 'supplement' && <DetailTypeBadge $type="supplement">보강</DetailTypeBadge>}
                          {item.type === 'event' && <DetailTypeBadge $type="event">학원 일정</DetailTypeBadge>}
                        </DetailItemName>
                        <DetailItemMeta>
                          {item.time && `${item.time}`}
                          {item.endTime && ` ~ ${item.endTime}`}
                          {item.teacherName && ` · 강사: ${item.teacherName}`}
                          {item.room && ` · ${item.room}`}
                          {item.studentCount > 0 && ` · 학생 ${item.studentCount}명`}
                        </DetailItemMeta>
                      </DetailItemInfo>
                      <DetailItemActions>
                        {(item.type === 'regular' || item.type === 'supplement') && item.classId && (
                          <ManageBtn onClick={() => navigate(`/admin/classes/${item.classId}`)}>
                            <HiOutlineCog6Tooth size={14} /> 클래스 관리하기
                          </ManageBtn>
                        )}
                        {item.type === 'event' && !isReadOnly && (
                          <EventDeleteBtn onClick={() => removeEvent(item.eventData)}>
                            <HiOutlineTrash size={14} /> 삭제
                          </EventDeleteBtn>
                        )}
                      </DetailItemActions>
                    </DetailItem>
                  ))}
                </DetailList>
              )}
            </DetailModal>
          </Overlay>
        );
      })()}
      {/* ═══ 학원 일정 추가 모달 ═══ */}
      {showEventModal && (
        <Overlay onClick={() => setShowEventModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>학원 일정 추가</ModalTitle>
              <ModalCloseBtn onClick={() => setShowEventModal(false)}><IoCloseCircle size={22} /></ModalCloseBtn>
            </ModalHeader>
            <FieldLabel>일정명 *</FieldLabel>
            <FieldInput value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder="" />
            <FieldRow>
              <div style={{ flex: 1, minWidth: 0 }}>
                <FieldLabel>날짜 *</FieldLabel>
                <FieldInput type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <FieldLabel>시작 시간</FieldLabel>
                <FieldInput type="time" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <FieldLabel>종료 시간</FieldLabel>
                <FieldInput type="time" value={eventForm.endTime} onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })} />
              </div>
            </FieldRow>
            <FieldLabel>설명</FieldLabel>
            <FieldInput value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} placeholder="" />
            <FieldLabel>색상</FieldLabel>
            <EventColorRow>
              {EVENT_COLORS.map((c) => (
                <EventColorBtn key={c} $color={c} $active={eventForm.color === c} onClick={() => setEventForm({ ...eventForm, color: c })} />
              ))}
            </EventColorRow>
            <ModalActions>
              <ModalCancelBtn onClick={() => setShowEventModal(false)}>취소</ModalCancelBtn>
              <ModalSaveBtn onClick={addEvent} disabled={!eventForm.title || !eventForm.date}>추가</ModalSaveBtn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminSchedulePage;

// ═══ Styled Components ═══
const Container = styled.div``;
const TopRow = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;gap:16px;`;
const TopRowCenter = styled.div`display:flex;align-items:center;gap:12px;`;
const TopRowRight = styled.div`display:flex;align-items:center;gap:10px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:700;color:#111827;white-space:nowrap;`;
const AddBtn = styled.button`display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;background:#4A7CFE;color:white;font-size:13px;font-weight:600;white-space:nowrap;&:hover{background:#3B6DE8;}`;

// View toggle
const ViewToggle = styled.div`display:flex;background:#F3F4F6;border-radius:10px;padding:3px;gap:2px;`;
const ViewBtn = styled.button`
  padding:7px 18px;border-radius:8px;font-size:13px;font-weight:600;transition:all 0.15s;
  ${({ $active }) => $active
    ? 'background:white;color:#374151;box-shadow:0 1px 3px rgba(0,0,0,0.08);'
    : 'background:transparent;color:#9CA3AF;&:hover{color:#6B7280;}'}
`;

// Nav
const NavBtn = styled.button`
  display:flex;align-items:center;justify-content:center;width:36px;height:36px;
  border-radius:8px;color:#6B7280;border:1px solid #E5E7EB;
  &:hover{background:#EEF2FF;color:#4A7CFE;border-color:#DBEAFE;}
`;
const NavTitle = styled.span`font-size:18px;font-weight:800;color:#111827;min-width:180px;text-align:center;`;
const TodayBtn = styled.button`
  padding:6px 14px;border-radius:8px;font-size:13px;font-weight:600;
  border:1px solid #E5E7EB;color:#6B7280;
  &:hover{background:#EEF2FF;color:#4A7CFE;border-color:#DBEAFE;}
`;

// ═══ 주간 ═══
const WeekCard = styled.div`background:white;border-radius:12px;border:1px solid #EAEDF3;overflow-x:auto;box-shadow:0 1px 3px rgba(0,0,0,0.04);`;
const WeekTable = styled.table`width:100%;border-collapse:collapse;min-width:800px;`;
const WTimeTh = styled.th`width:60px;padding:12px 8px;font-size:12px;color:#9CA3AF;font-weight:600;background:#FAFBFC;`;
const WDayTh = styled.th`
  padding:12px 8px;text-align:center;
  background:${({ $today }) => $today ? '#EEF2FF' : '#FAFBFC'};
`;
const WDayName = styled.div`
  font-size:13px;font-weight:700;
  color:${({ $today, $sun, $sat }) => $today ? '#4A7CFE' : $sun ? '#EF4444' : $sat ? '#4A7CFE' : '#374151'};
`;
const WDayDate = styled.div`
  font-size:12px;margin-top:2px;
  color:${({ $today }) => $today ? '#4A7CFE' : '#9CA3AF'};
  font-weight:${({ $today }) => $today ? 700 : 400};
`;
const WTimeCell = styled.td`padding:6px 8px;font-size:12px;color:#9CA3AF;text-align:center;vertical-align:top;height:52px;`;
const WDayCell = styled.td`
  padding:4px;vertical-align:top;height:52px;
  background:${({ $today }) => $today ? '#FAFBFF' : 'transparent'};
`;
const WBlock = styled.div`
  padding:6px 8px;border-radius:6px;margin-bottom:2px;
  background:${({ $supplement, $event }) => $supplement ? '#FEE2E2' : $event ? '#EDE9FE' : '#F3F4F6'};
  border-left:3px solid ${({ $color, $supplement, $event }) => $supplement ? '#DC2626' : $event ? '#6366F1' : '#D1D5DB'};
`;
const WBlockTop = styled.div`display:flex;justify-content:space-between;align-items:flex-start;`;
const WBlockName = styled.div`font-size:12px;font-weight:700;color:#111827;flex:1;`;
const WBlockTime = styled.div`font-size:11px;color:#6B7280;margin-top:1px;`;
const WBlockRoom = styled.div`font-size:10px;color:#9CA3AF;`;
const WDeleteBtn = styled.button`display:flex;color:#D1D5DB;padding:1px;&:hover{color:#EF4444;}`;
const WEventBadge = styled.span`font-size:9px;font-weight:700;color:#6366F1;background:#EDE9FE;padding:1px 5px;border-radius:3px;margin-top:2px;display:inline-block;`;

// ═══ 월간 ═══
const MonthCard = styled.div`background:white;border-radius:12px;border:1px solid #EAEDF3;padding:20px;box-shadow:0 1px 3px rgba(0,0,0,0.04);`;
const MWeekRow = styled.div`display:grid;grid-template-columns:repeat(7,1fr);margin-bottom:6px;border-bottom:1px solid #F3F4F6;padding-bottom:8px;`;
const MWeekCell = styled.div`
  text-align:center;font-size:16px;font-weight:700;padding:12px 0;
  color:${({ $sun, $sat }) => $sun ? '#EF4444' : $sat ? '#4A7CFE' : '#9CA3AF'};
`;
const MGrid = styled.div`display:grid;grid-template-columns:repeat(7,1fr);`;
const MDayCell = styled.div`
  min-height:140px;padding:12px;border:1px solid #F3F4F6;transition:all 0.15s;
  ${({ $selected }) => $selected && 'background:#EEF2FF;border-color:#4A7CFE;'}
  &:hover{background:${({ $selected }) => $selected ? '#EEF2FF' : '#FAFBFC'};}
`;
const MDayNum = styled.span`
  font-size:18px;font-weight:700;display:block;margin-bottom:6px;
  color:${({ $sun, $sat, $today }) => $today ? 'white' : $sun ? '#EF4444' : $sat ? '#4A7CFE' : '#374151'};
  ${({ $today }) => $today && `
    display:inline-flex;align-items:center;justify-content:center;
    background:#4A7CFE;border-radius:50%;width:32px;height:32px;
  `}
`;
const MEventList = styled.div`display:flex;flex-direction:column;gap:4px;`;
const MEventTag = styled.div`
  display:flex;align-items:center;gap:5px;padding:5px 10px;border-radius:6px;
  background:${({ $event, $supplement }) => $event ? '#EDE9FE' : $supplement ? '#FEE2E2' : '#F3F4F6'};
  ${({ $event }) => $event && 'border:1px solid #C4B5FD;'}
`;
const MEventDot = styled.div`width:7px;height:7px;border-radius:50%;background:#D1D5DB;flex-shrink:0;`;
const MSupDot = styled.div`width:7px;height:7px;border-radius:50%;background:#DC2626;flex-shrink:0;`;
const MEventEmoji = styled.span`font-size:12px;flex-shrink:0;`;
const MEventText = styled.span`font-size:13px;font-weight:${({ $event }) => $event ? 700 : 600};color:${({ $event }) => $event ? '#5B21B6' : '#374151'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
const MSupBadge = styled.span`
  font-size:11px;font-weight:700;padding:2px 6px;border-radius:4px;white-space:nowrap;margin-left:auto;
  color:${({ $event }) => $event ? '#6366F1' : '#DC2626'};
  background:${({ $event }) => $event ? '#EDE9FE' : '#FECACA'};
`;

// Legend
const LegendRow = styled.div`display:flex;flex-wrap:wrap;gap:12px;margin-top:16px;padding:12px 16px;background:white;border-radius:10px;border:1px solid #EAEDF3;`;
const LegendItem = styled.div`display:flex;align-items:center;gap:6px;`;
const LegendDot = styled.div`width:10px;height:10px;border-radius:50%;background:${({ $color }) => $color};`;
const LegendText = styled.span`font-size:13px;font-weight:600;color:#374151;`;

// Detail modal
const DetailModal = styled.div`
  background:white;border-radius:16px;padding:24px;width:560px;max-width:90vw;max-height:80vh;overflow-y:auto;
  box-shadow:0 20px 60px rgba(0,0,0,0.15);
`;
const DetailHeader = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;`;
const DetailTitle = styled.span`font-size:16px;font-weight:700;color:#111827;`;
const DetailCloseBtn = styled.button`display:flex;color:#9CA3AF;&:hover{color:#6B7280;}`;
const DetailEmpty = styled.p`text-align:center;padding:16px 0;font-size:14px;color:#9CA3AF;`;
const DetailList = styled.div`display:flex;flex-direction:column;gap:10px;`;
const DetailItem = styled.div`
  display:flex;align-items:center;gap:14px;padding:14px 16px;
  background:#FAFBFC;border-radius:10px;border:1px solid #F3F4F6;
`;
const DetailColorBar = styled.div`width:4px;height:40px;border-radius:2px;background:${({ $color }) => $color};flex-shrink:0;`;
const DetailItemInfo = styled.div`flex:1;min-width:0;`;
const DetailItemName = styled.p`font-size:15px;font-weight:700;color:#111827;margin:0;display:flex;align-items:center;gap:8px;`;
const DetailItemMeta = styled.p`font-size:13px;color:#6B7280;margin:3px 0 0;`;
const DetailTypeBadge = styled.span`
  font-size:11px;font-weight:700;padding:2px 8px;border-radius:4px;
  ${({ $type }) => $type === 'supplement'
    ? 'color:#DC2626;background:#FEE2E2;'
    : 'color:#6366F1;background:#EDE9FE;'}
`;
const DetailItemActions = styled.div`display:flex;align-items:center;gap:8px;flex-shrink:0;`;
const ManageBtn = styled.button`
  display:flex;align-items:center;gap:5px;padding:8px 14px;border-radius:8px;
  font-size:13px;font-weight:600;color:#4A7CFE;background:#EEF2FF;
  &:hover{background:#DBEAFE;}
`;
const EventDeleteBtn = styled.button`
  display:flex;align-items:center;gap:4px;padding:8px 14px;border-radius:8px;
  font-size:13px;font-weight:600;color:#EF4444;background:#FEF2F2;
  &:hover{background:#FEE2E2;}
`;

// Modal
const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:50;`;
const Modal = styled.div`background:white;border-radius:16px;padding:24px;width:480px;max-width:90vw;`;
const ModalHeader = styled.div`display:flex;justify-content:space-between;align-items:center;padding:20px 24px;background:#EEF2FF;border-bottom:1px solid #D9E2FF;margin-bottom:0;`;
const ModalTitle = styled.h2`font-size:18px;font-weight:700;color:#1E3A5F;margin:0;`;
const ModalCloseBtn = styled.button`display:flex;color:#9CA3AF;&:hover{color:#6B7280;}`;
const FieldLabel = styled.label`display:block;font-size:13px;font-weight:600;color:#374151;margin-bottom:6px;margin-top:14px;`;
const FieldInput = styled.input`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;box-sizing:border-box;&:focus{border-color:#4A7CFE;outline:none;}`;
const FieldRow = styled.div`display:flex;gap:12px;`;
const EventColorRow = styled.div`display:flex;gap:8px;margin-top:4px;`;
const EventColorBtn = styled.button`
  width:28px;height:28px;border-radius:50%;background:${({ $color }) => $color};cursor:pointer;
  border:3px solid ${({ $active, $color }) => $active ? $color : 'transparent'};
  outline:${({ $active }) => $active ? '2px solid #E5E7EB' : 'none'};outline-offset:1px;
`;
const ModalActions = styled.div`display:flex;gap:8px;justify-content:flex-end;margin-top:20px;`;
const ModalCancelBtn = styled.button`padding:10px 20px;border-radius:8px;border:1px solid #E5E7EB;font-size:14px;color:#374151;&:hover{background:#F9FAFB;}`;
const ModalSaveBtn = styled.button`padding:10px 20px;border-radius:8px;background:#4A7CFE;color:white;font-size:14px;font-weight:600;&:hover{background:#3B6DE8;}&:disabled{opacity:0.5;}`;
